import moment from 'moment';

import { getInstallmentCompetence } from './filterParams';

export type BankSlipStatusLabel = 'À vencer' | 'Vencido' | 'Pago';

export type BankSlipRow = {
  invoice: {
    invoiceId: string;
    id?: string;
    invoiceNumber?: string | number;
    accessKey?: string;
    totalAmount?: number | string;
    emissionDate?: string;
    source?: string;
    customer?: {
      code: string;
      siteCode: string;
    };
  };
  installment: {
    installmentId: string;
    typeableLine?: string;
    amountOriginal?: number | string;
    dueDate: string;
    paymentStatus?: string;
    status?: string;
    paymentTypeBoleto?: boolean;
    referenceMonth?: string;
  };
};

export const getBoletoNumber = (typeableLine?: string): string => {
  if (!typeableLine) return '';

  return typeableLine.replace(/\D/g, '');
};

export const BOLETO_NUMBER_DISPLAY_MAX = 30;

export const formatBoletoNumberDisplay = (
  typeableLine?: string,
  maxLength = BOLETO_NUMBER_DISPLAY_MAX
): { display: string; full: string; isTruncated: boolean } => {
  const full = getBoletoNumber(typeableLine);

  if (!full) {
    return { display: '-', full: '', isTruncated: false };
  }

  if (full.length <= maxLength) {
    return { display: full, full, isTruncated: false };
  }

  return {
    display: `${full.slice(0, maxLength)}...`,
    full,
    isTruncated: true,
  };
};

export const formatInstallmentAmount = (
  installment: BankSlipRow['installment'],
  invoice: BankSlipRow['invoice']
): string => {
  const rawAmount =
    installment.amountOriginal !== undefined && installment.amountOriginal !== null
      ? installment.amountOriginal
      : invoice.totalAmount;

  if (rawAmount === undefined || rawAmount === null || rawAmount === '') {
    return '-';
  }

  return Number(rawAmount).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const formatDueDate = (dueDate?: string): string => {
  if (!dueDate) return '-';

  return moment(dueDate).format('DD/MM/YYYY');
};

export const formatInvoiceNumber = (
  invoiceNumber?: string | number | null
): string => {
  if (invoiceNumber === undefined || invoiceNumber === null || invoiceNumber === '') {
    return '-';
  }

  return String(invoiceNumber);
};

export const formatCompetenceLabel = (
  installment: BankSlipRow['installment'],
  invoice: BankSlipRow['invoice'],
  customerType?: string | null
): string => {
  const competence = getInstallmentCompetence(installment, invoice, customerType);

  if (!competence) return '-';

  const parsed = moment(competence, 'YYYY/MM', true);

  if (!parsed.isValid()) return competence;

  const label = parsed.format('MMM/YYYY').replace('.', '');

  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getInstallmentStatus = (
  installment: BankSlipRow['installment']
): BankSlipStatusLabel => {
  const currentDate = new Date();
  const dueDate = new Date(installment.dueDate);
  dueDate.setDate(dueDate.getDate() + 1);

  if (installment.paymentStatus === 'OP') {
    return currentDate > dueDate ? 'Vencido' : 'À vencer';
  }

  return 'Pago';
};

export const getStatusBadgeVariant = (
  status: BankSlipStatusLabel
): 'warning' | 'danger' | 'success' => {
  if (status === 'Vencido') return 'danger';
  if (status === 'Pago') return 'success';

  return 'warning';
};

export type StatusBadgeTheme = {
  backgroundColor: string;
  borderColor: string;
  color: string;
};

export const getStatusBadgeTheme = (
  status: BankSlipStatusLabel
): StatusBadgeTheme => {
  if (status === 'Vencido') {
    return {
      backgroundColor: '#FFF0F0',
      borderColor: '#E34E2A',
      color: '#8B2E2E',
    };
  }

  if (status === 'Pago') {
    return {
      backgroundColor: '#EFF8EF',
      borderColor: '#75B943',
      color: '#2E6B2E',
    };
  }

  return {
    backgroundColor: '#FFF8E6',
    borderColor: '#F0B01B',
    color: '#9A7209',
  };
};

export const isAvailableForDownload = (
  installment: BankSlipRow['installment'],
  invoice: BankSlipRow['invoice']
): boolean => {
  if (getInstallmentStatus(installment) === 'Pago') {
    return false;
  }

  const hasBoleto =
    Boolean(installment.typeableLine) &&
    (installment.paymentTypeBoleto ?? true);

  return Boolean(hasBoleto && invoice.accessKey !== undefined);
};
